import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import Button from '../../../common/components/Button/Button';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { GetEmailDto } from '../../../common/pokCore/autogenerated/pokApiClient';

import { MailTeamForm } from './MailTeamForm';

export interface MailTeamParams {
  teamId?: string;
  employeesIds: string[];
  addTeamToEmails: boolean;
  message: string;
  rangeMonth: [Date | null, Date | null] | null;
}

interface MailTeamModalProps {
  handleClose: () => void;
  show?: boolean;
  tv: boolean;
  selectedIds?: string[];
  invoiced?: boolean;
  month?: Date;
  isPurchase?: boolean;
  sendFunction?: (
    tv: boolean,
    email: string[],
    message: string,
    teamId?: string,
    dateFrom?: Date,
    dateTo?: Date,
    selectedId?: string[],
  ) => Promise<GetEmailDto>;
  sendFunction2?: (
    email: string[],
    message: string,
    teamId?: string,
    invoiced?: boolean,
    month?: Date,
  ) => Promise<GetEmailDto>;
}

export const MailTeamModal: React.FC<MailTeamModalProps> = props => {
  const { t, tk } = useTranslation('commonLng');
  const notifications = useNotifications();
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [emailParams, setEmailParams] = useState<MailTeamParams>({
    teamId: '',
    employeesIds: [],
    addTeamToEmails: false,
    message: '',
    rangeMonth: null,
  });
  const [emails, setEmails] = useState<string[]>([]);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
    } else {
      setEmailParams({
        teamId: '',
        employeesIds: [],
        addTeamToEmails: false,
        message: '',
        rangeMonth: null,
      });
      setEmails([]);
    }
  }, [props.show]);

  const sendEmail = async () => {
    setIsLoadingSave(true);
    if (props.sendFunction) {
      props
        .sendFunction(
          props.tv,
          emails,
          emailParams.message,
          emailParams.teamId,
          emailParams.rangeMonth?.[0] || undefined,
          emailParams.rangeMonth?.[1] || undefined,
          props.selectedIds,
        )
        .then(() => props.handleClose())
        .catch(err => {
          notifications.caughtError(err);
        })
        .finally(() => setIsLoadingSave(false));
    }
    if (props.sendFunction2) {
      props
        .sendFunction2(
          emails,
          emailParams.message,
          emailParams.teamId,
          props.invoiced,
          props.month,
        )
        .then(() => props.handleClose())
        .catch(err => {
          notifications.caughtError(err);
        })
        .finally(() => setIsLoadingSave(false));
    }
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t(tk.email.create)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MailTeamForm
            emailParams={emailParams}
            setEmailParams={setEmailParams}
            emails={emails}
            setEmails={setEmails}
            tv={props.tv}
            selectedIds={props.selectedIds}
            isPurchase={props.isPurchase}
          />
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex marginLeft gap-2">
              <Button
                variant="outline-secondary"
                onClick={props.handleClose}
                disabled={isLoadingSave}
              >
                {t(tk.button.close)}
              </Button>

              <Button
                variant="primary"
                onClick={sendEmail}
                isLoading={isLoadingSave}
                disabled={
                  emailParams.message === '' ||
                  emails.length === 0 ||
                  ((!emailParams.teamId || emailParams.teamId === '') &&
                    !props.selectedIds?.length)
                }
              >
                {t(tk.email.send)}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
